import { installBaggagePropagation } from './baggage.js'
import { clearConsent, consentKey, hasAnalyticsConsent, writeConsent } from './consent.js'
import { startConsoleForward, type ConsoleForwardHandle } from './console-forward.js'
import { startJsErrorCapture, type JsErrorHandle } from './js-errors.js'
import { shutdownOtelLogs } from './otel-logs.js'
import { setupOtelMetrics, type MetricsHandle } from './otel-metrics.js'
import { setupOtelRum, shutdownOtelRum, telemetryIgnoreUrls } from './otel-rum.js'
import { startPageViews, type PageViewsHandle } from './page-views.js'
import { startReplayRecording, type ReplayRecorder } from './rrweb-record.js'
import { startSemanticEvents, type SemanticEventsHandle } from './semantic-events.js'
import { startSessionLifecycle, type SessionLifecycleHandle } from './session-lifecycle.js'
import { configureSession, ensureSession, getSessionId } from './session.js'
import { clearUser, configureUser, getPseudoUserId, setUser } from './user.js'
import type { ObservInitOptions, ObservSdk } from './types.js'

export type { ObservInitOptions, ObservSdk, ObservUser } from './types.js'
export { setUser, clearUser } from './user.js'

/**
 * Attribute key used everywhere to correlate signals of a single session.
 * Re-exported from {@link ./constants.js} (the leaf module that internal modules
 * also import, avoiding an `index ↔ otel-rum` cycle). EXACTLY `session.id`.
 */
export { SESSION_ID_ATTRIBUTE } from './constants.js'

/** Singleton replay recorder handle (one capture per page, like the OTel provider). */
let replayRecorder: ReplayRecorder | null = null
/** Singleton semantic-events tap (story 2.3). */
let semanticEvents: SemanticEventsHandle | null = null
/** Singleton JS-error capture (story 2.4). */
let jsErrors: JsErrorHandle | null = null
/** Singleton metrics handle — Core Web Vitals + OTLP metrics (parity). */
let metrics: MetricsHandle | null = null
/** Baggage-propagation uninstaller, set when `propagateBaggage` is on (parity). */
let baggageUninstall: (() => void) | null = null
/** Singleton session-lifecycle wiring (start/end events + activity + sweep). */
let sessionLifecycle: SessionLifecycleHandle | null = null
/** Singleton console forwarder, set when `forwardConsole` is on (parity). */
let consoleForward: ConsoleForwardHandle | null = null
/** Singleton page-view tracker — `app.page_view` load + SPA events (parity). */
let pageViews: PageViewsHandle | null = null
/** Options held while a `requireConsent` init waits for consent (else null). */
let pendingConsentOptions: ObservInitOptions | null = null
/** Options of the last started SDK — lets {@link revokeConsent} find the consent
 *  key after the SDK has already started (no longer "pending"). */
let lastStartedOptions: ObservInitOptions | null = null
/** Tears down the consent watcher (storage listener + poll), if installed. */
let consentWatcherCleanup: (() => void) | null = null

/** Poll cadence while waiting for same-tab consent (storage events only fire
 *  cross-tab, so a poll covers the banner writing consent in THIS tab). */
const CONSENT_POLL_MS = 1_500

/**
 * Initialize the Observ RUM SDK.
 *
 *   1. Establishes (or resumes) the visit's `session.id` — the single
 *      correlation key shared by every signal (story 1.2).
 *   2. Wires OTel RUM tracing (story 1.3): `http.client` spans for fetch/XHR,
 *      carrying `session.id`, with W3C `traceparent` propagation and OTLP/HTTP
 *      export to `{endpoint}/v1/traces`.
 *   3. Starts rrweb replay capture (story 2.2): buffered gzip chunks POSTed to
 *      `{endpoint}/v1/replay` (size/time triggered), unless `disableReplay`.
 *   4. Derives semantic events (story 2.3): `click` / `rage_click` / `navigate`
 *      emitted as OTel log records to `{endpoint}/v1/logs` (independent of
 *      `disableReplay`).
 *   5. Captures JS errors (story 2.4): `observ.session.js_error` (uncaught
 *      errors + unhandled rejections) on the same logs channel.
 *
 * Idempotent (a second call re-registers nothing) and never throws — any setup
 * failure is swallowed so the SDK can never break the host page; it degrades to
 * "no tracing / no replay / no semantic events".
 *
 * When `requireConsent` is set, init becomes a GDPR consent barrier: it does
 * nothing (no session, no persistent id, no providers, no network) until consent
 * is present, then auto-starts — watching for a banner that grants consent later
 * (cross-tab via `storage`, same-tab via a light poll) or {@link grantConsent}.
 */
export function init(options: ObservInitOptions): void {
  try {
    if (options.requireConsent && !hasAnalyticsConsent(options)) {
      // Gate everything until consent arrives. Remember the options and watch.
      pendingConsentOptions = options
      installConsentWatcher(options)
      return
    }
    startSdk(options)
  } catch (err) {
    // The SDK must never break the host page (1.1/1.2 guarantee).
    console.warn('[observ] RUM init failed; telemetry disabled.', err)
  }
}

/** Wire every layer of the SDK. Assumes consent (if required) is granted. */
function startSdk(options: ObservInitOptions): void {
  try {
    lastStartedOptions = options
    // Apply session tuning (inactivity / max-duration / cross-tab) BEFORE the
    // session is first resolved, then start the lifecycle wiring so its listener
    // is subscribed in time to capture the very first `session.start`. The
    // bootstrap read (`ensureSession`) comes right after.
    configureSession({
      inactivityTimeoutMs: options.sessionInactivityMs,
      maxDurationMs: options.sessionMaxDurationMs,
      crossTab: options.crossTabSessions,
    })
    // End-user identity: honour the pseudo-user opt-out, then mint/restore the
    // pseudonymous id once so `enduser.pseudo.id` is ready for the first signal.
    configureUser({ disablePseudoUser: options.disablePseudoUser })
    getPseudoUserId()
    if (!sessionLifecycle) {
      sessionLifecycle = startSessionLifecycle(options)
    }
    ensureSession()
    setupOtelRum(options)
    if (!options.disableReplay && !replayRecorder) {
      replayRecorder = startReplayRecording(options)
    }
    // Semantic events + JS errors are useful even without replay → not gated.
    if (!semanticEvents) {
      semanticEvents = startSemanticEvents(options)
    }
    // Rich page views (parity) — independent of replay; opt-out via `disablePageViews`.
    if (!options.disablePageViews && !pageViews) {
      pageViews = startPageViews(options)
    }
    if (!jsErrors) {
      jsErrors = startJsErrorCapture(options)
    }
    // Console forwarding (parity) — opt-in via `forwardConsole`; mirrors console.*
    // to the same logs channel. startConsoleForward no-ops when the option is off.
    if (options.forwardConsole && !consoleForward) {
      consoleForward = startConsoleForward(options)
    }
    // Metrics + Core Web Vitals (parity) — independent of replay; opt-out via `disableMetrics`.
    if (!options.disableMetrics && !metrics) {
      metrics = setupOtelMetrics(options)
    }
    // Baggage: stamp `session.id` on requests to the same backends that receive
    // `traceparent` (+ same-origin), so backend spans can read the session identity.
    if (options.propagateBaggage && !baggageUninstall) {
      const backends = [...(options.propagateTraceHeaderCorsUrls ?? [])]
      if (typeof location !== 'undefined') backends.push(location.origin)
      baggageUninstall = installBaggagePropagation(
        getSessionId,
        backends,
        telemetryIgnoreUrls(options.endpoint),
      )
    }
  } catch (err) {
    // The SDK must never break the host page (1.1/1.2 guarantee). Degrade to
    // "no tracing / no replay / no semantic events" rather than propagating.
    console.warn('[observ] RUM setup failed; tracing/replay/events degraded.', err)
  }
}

/** Remove the consent watcher (storage listener + poll), if any. */
function removeConsentWatcher(): void {
  const cleanup = consentWatcherCleanup
  consentWatcherCleanup = null
  try {
    cleanup?.()
  } catch {
    /* swallow */
  }
}

/** Start the deferred SDK once consent appears; tears the watcher down first. */
function startFromConsent(options: ObservInitOptions): void {
  removeConsentWatcher()
  pendingConsentOptions = null
  startSdk(options)
}

/**
 * Watch for consent granted AFTER a gated init: cross-tab via the `storage` event
 * (the banner writing the key in another tab) and same-tab via a light poll (a
 * same-tab `localStorage` write fires no `storage` event). Either way, the SDK
 * starts the instant consent is present. {@link grantConsent} is the explicit path.
 */
function installConsentWatcher(options: ObservInitOptions): void {
  if (consentWatcherCleanup || typeof window === 'undefined') return
  const key = consentKey(options)
  const tryStart = (): void => {
    if (hasAnalyticsConsent(options)) startFromConsent(options)
  }
  const onStorage = (e: StorageEvent): void => {
    if (e.key === key) tryStart()
  }
  window.addEventListener('storage', onStorage)
  const interval = setInterval(tryStart, CONSENT_POLL_MS)
  consentWatcherCleanup = (): void => {
    window.removeEventListener('storage', onStorage)
    clearInterval(interval)
  }
}

/**
 * Grant analytics consent at runtime: persist the consent value and immediately
 * start a `requireConsent` init that was waiting. No-op when nothing is gated.
 */
export function grantConsent(): void {
  const options = pendingConsentOptions
  if (!options) return
  writeConsent(options) // persist so the next visit starts without re-prompting
  startFromConsent(options)
}

/**
 * Revoke analytics consent: clear the stored consent (so future inits gate again)
 * and shut the SDK down. Already-sent data cannot be recalled. A later `init()` /
 * {@link grantConsent} re-arms it.
 */
export function revokeConsent(): void {
  const options = pendingConsentOptions ?? lastStartedOptions
  if (options) clearConsent(options)
  removeConsentWatcher()
  pendingConsentOptions = null
  void shutdown()
}

/**
 * Stop replay capture (flushing the residual chunk), the semantic-event tap, and
 * tear down OTel tracing + logs. Best-effort and never throws (resolves the
 * story 1.3 "no public teardown" deferral). After this, a later {@link init} can
 * re-start the SDK.
 */
export async function shutdown(): Promise<void> {
  // Drop any unsatisfied consent gate so a stale watcher can't auto-start later.
  removeConsentWatcher()
  pendingConsentOptions = null
  const recorder = replayRecorder
  const events = semanticEvents
  const errors = jsErrors
  const metricsHandle = metrics
  const uninstallBaggage = baggageUninstall
  const lifecycle = sessionLifecycle
  const consoleHandle = consoleForward
  const pageViewsHandle = pageViews
  replayRecorder = null
  semanticEvents = null
  jsErrors = null
  metrics = null
  baggageUninstall = null
  sessionLifecycle = null
  consoleForward = null
  pageViews = null
  try {
    // Restore the patched console BEFORE the logs provider is shut down, so a
    // late console call can't try to emit through a dead provider.
    consoleHandle?.stop()
  } catch {
    /* swallow — restoring console must never throw */
  }
  try {
    pageViewsHandle?.stop()
  } catch {
    /* swallow — unsubscribing the history observer must never throw */
  }
  try {
    lifecycle?.stop()
  } catch {
    /* swallow — removing listeners / clearing the sweep timer must never throw */
  }
  try {
    uninstallBaggage?.()
  } catch {
    /* swallow — restoring window.fetch must never throw */
  }
  try {
    await recorder?.stop()
  } catch {
    /* swallow — teardown must never throw to the host page */
  }
  try {
    await events?.stop()
  } catch {
    /* swallow */
  }
  try {
    errors?.stop()
  } catch {
    /* swallow */
  }
  try {
    await metricsHandle?.stop()
  } catch {
    /* swallow */
  }
  // Both the semantic-events tap and the JS-error capture share ONE logs
  // provider — shut it down once, here, after both taps have stopped. Each
  // provider shutdown is guarded so a flush rejection can't skip the next one
  // or escape to the host page (shutdown must never throw — 1.3 guarantee).
  try {
    await shutdownOtelLogs()
  } catch {
    /* swallow */
  }
  try {
    await shutdownOtelRum()
  } catch {
    /* swallow */
  }
}

/** Singleton SDK handle. Usage: `observ.init({ endpoint, key })`. */
export const observ: ObservSdk = {
  init,
  shutdown,
  setUser,
  clearUser,
  grantConsent,
  revokeConsent,
}

export default observ
